import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'wouter';
import { ArrowRight, Download } from 'lucide-react';
import { api } from '../../lib/api';
import { activeSpaceHref } from '../../hooks/useCurrentSpace';
import type { AppUser } from '../../types';

// Shared call-to-action pair for public pages. For logged-out visitors the
// primary button hits the server OAuth route (full navigation). For logged-in
// visitors — who can reach the landing via the in-app logo — it instead links
// back into the app. The secondary always points to the download page.
export function PublicCtaButtons() {
  const { t } = useTranslation();
  const [user, setUser] = useState<AppUser | null>(null);

  useEffect(() => {
    api.getMe().then(setUser).catch(() => {});
  }, []);

  const primaryClass =
    'inline-flex items-center justify-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-violet-600 to-fuchsia-500 hover:from-violet-700 hover:to-fuchsia-600 transition-colors shadow-sm w-full sm:w-auto outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-50 dark:focus-visible:ring-offset-zinc-950';

  return (
    <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
      {user ? (
        <Link href={activeSpaceHref()} className={primaryClass}>
          {t('home.hero.ctaOpenApp')}
          <ArrowRight className="w-4 h-4" aria-hidden />
        </Link>
      ) : (
        <a href="/signup" className={primaryClass}>
          {t('home.hero.ctaPrimary')}
          <ArrowRight className="w-4 h-4" aria-hidden />
        </a>
      )}
      <Link
        href="/download"
        className="inline-flex items-center justify-center gap-2 rounded-lg px-5 py-2.5 text-sm font-medium text-zinc-700 dark:text-zinc-200 border border-zinc-300 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors w-full sm:w-auto outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-50 dark:focus-visible:ring-offset-zinc-950"
      >
        <Download className="w-4 h-4" aria-hidden />
        {t('home.hero.ctaSecondary')}
      </Link>
    </div>
  );
}
